import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Zap, Mail } from 'lucide-react';
import { FgButton } from '@/components/fg-button';
import { FgInput } from '@/components/fg-input';
import { FgToast } from '@/components/fg-toast';
import { api, setToken } from '@/lib/api';
import { AccentDot, Widget } from '@/components/Widget';

/**
 * Admin portal login — email + password.
 *
 * Authenticates via `POST /api/auth/login` (the bcrypt-backed
 * endpoint). On success the response carries `{ accessToken, user }`.
 * We persist both in localStorage (cookie-free for simplicity in this
 * admin surface) and bounce to the dashboard.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('admin@fullgerman.com');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { data } = await api.post('/api/auth/login', {
        email: email.trim().toLowerCase(),
        password,
      });
      setToken(data.accessToken, data.user);
      FgToast.success(`Welcome, ${data.user.fullName}`);
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-6">
      <div className="w-full max-w-md">
        {/* ── Brand header ──────────────────────────────────────── */}
        <div className="flex flex-col items-center text-center mb-6">
          <AccentDot accent="yellow" size="lg">
            <Zap size={22} />
          </AccentDot>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
            FullGerman <span className="text-[#FFD60A]">Admin</span>
          </h1>
          <p className="mt-2 text-sm text-white/55 max-w-xs">
            Command-center access for the curriculum, learners and platform
            economy.
          </p>
        </div>

        <Widget accent="yellow" hero>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-[11px] font-mono font-bold uppercase tracking-wider text-white/65 mb-1.5"
              >
                Email
              </label>
              <FgInput
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-[11px] font-mono font-bold uppercase tracking-wider text-white/65 mb-1.5"
              >
                Password
              </label>
              <FgInput
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                hint="Min 8 characters."
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-xs text-red-300">
                <Mail size={12} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <FgButton
              type="submit"
              loading={busy}
              size="lg"
              className="w-full fg-cta-primary"
              leftIcon={<Lock size={14} />}
            >
              Sign in to admin
            </FgButton>
          </form>
        </Widget>

        <p className="mt-6 text-center text-[11px] text-white/45 max-w-md">
          Lost access? Ask the superadmin to reset your password via
          <code className="mx-1 rounded bg-white/5 px-1 text-[10px] text-white/80">
            seed-admin.ts
          </code>
          or by calling <code className="ml-1 rounded bg-white/5 px-1 text-[10px] text-white/80">setPassword()</code> on the UsersService.
        </p>
      </div>
    </main>
  );
}